import { useQuery } from "@apollo/client";
import { useAbility } from "@casl/react";
import { CodeSnippet, IconButton, Modal, useModal } from "@fluidattacks/design";
import _ from "lodash";
import { Fragment, useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { AdditionalInfo } from "../../additional-info";
import { CustomFix } from "../../custom-fix";
import { SeverityInfo } from "../../severity-info";
import { SeverityRequestInfo } from "../../severity-request-info";
import { TreatmentTracking } from "../../tracking-treatment";
import { TreatmentForm } from "../../treatment-modal/update-treatment";
import type {
  IUpdateVulnerabilityForm,
  IVulnerabilityModalValues,
} from "../../types";
import { defaultValue, updateDescriptionContext } from "../context";
import { VulnerabilityModalHeaderDescription } from "../header/description";
import { VulnerabilityModalHeaderTitle } from "../header/title";
import { GET_VULN_CODE_INFO } from "../queries";
import type { IVulnerabilityModal, TVulnsTabs } from "../types";
import { Authorize } from "components/@core/authorize";
import { authzPermissionsContext } from "context/authz/config";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

const ModalContent: FC<IVulnerabilityModal> = ({
  closeModal,
  currentRow,
  cvssVersion,
  findingId,
  groupName,
  isModalOpen,
  isFindingReleased,
  refetchData,
  severityUpdateRequest,
  onActionCompleted,
}: Readonly<IVulnerabilityModal>): JSX.Element => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState<TVulnsTabs>("details");
  const [treatmentForm, setTreatmentForm] =
    useState<IUpdateVulnerabilityForm>(defaultValue);
  const value = useMemo(
    (): IVulnerabilityModalValues => [treatmentForm, setTreatmentForm],
    [treatmentForm, setTreatmentForm],
  );
  const labels = useMemo(
    (): Record<string, string> => ({
      REJECTED: t("searchFindings.tabVuln.rejected"),
      SAFE: t("searchFindings.tabVuln.closed"),
      SUBMITTED: t("searchFindings.tabVuln.submitted"),
      VULNERABLE: t("searchFindings.tabVuln.open"),
    }),
    [t],
  );

  const modalProps = useModal("vulnerability-modal");
  const permissions = useAbility(authzPermissionsContext);

  const canRemoveVulnsTags = permissions.can(
    "integrates_api_mutations_remove_vulnerability_tags_mutate",
  );
  const canRequestZeroRiskVuln = permissions.can(
    "integrates_api_mutations_request_vulnerabilities_zero_risk_mutate",
  );
  const canSeeSource = permissions.can("see_vulnerability_source");
  const canUpdateVulnsTreatment = permissions.can(
    "integrates_api_mutations_update_vulnerabilities_treatment_mutate",
  );
  const canDisplayHacker = permissions.can(
    "integrates_api_resolvers_vulnerability_hacker_resolve",
  );

  const { data } = useQuery(GET_VULN_CODE_INFO, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning("An error occurred loading the code info", error);
      });
    },
    skip: !isModalOpen,
    variables: {
      vulnId: currentRow.id,
    },
  });

  const snippet =
    _.isUndefined(data) || _.isEmpty(data) ? null : data.vulnerability.snippet;
  const specific =
    _.isUndefined(data) || _.isEmpty(data) ? "" : data.vulnerability.specific;
  const isAutoFixable =
    _.isUndefined(data) || _.isEmpty(data)
      ? false
      : data.vulnerability.isAutoFixable;

  const onCopy = useCallback(async (): Promise<void> => {
    const { clipboard } = navigator;

    if (_.isUndefined(clipboard)) {
      msgError(t("searchFindings.copyUrl.failed"));
    } else {
      await clipboard.writeText(window.location.href);
      msgSuccess(
        t("searchFindings.copyUrl.success"),
        t("searchFindings.copyUrl.successTitle"),
      );
    }
  }, [t]);
  const onClose = useCallback((): void => {
    setTreatmentForm(defaultValue);
    closeModal();
  }, [closeModal]);
  const toTab = useCallback((event: React.MouseEvent): void => {
    event.preventDefault();

    const target = event.target as HTMLButtonElement;
    setCurrentTab(target.id as TVulnsTabs);
  }, []);

  const handleSeverityInfo = (isSeverityRequestInfo?: boolean): JSX.Element => {
    return (isSeverityRequestInfo ?? false) ? (
      <SeverityRequestInfo vulnerabilityId={currentRow.id} />
    ) : (
      <SeverityInfo
        cvssVersion={cvssVersion}
        findingId={currentRow.findingId}
        refetchData={refetchData}
        vulnerabilityId={currentRow.id}
      />
    );
  };

  const tabContent: Record<TVulnsTabs, JSX.Element> = {
    code: (
      <CodeSnippet
        snippet={snippet ?? { content: "", offset: 0 }}
        specific={specific}
        variant={"location"}
      />
    ),
    details: (
      <AdditionalInfo
        canRetrieveHacker={canDisplayHacker}
        canSeeSource={canSeeSource}
        refetchData={refetchData}
        vulnerability={currentRow}
      />
    ),
    howtofix: (
      <CustomFix
        findingId={findingId}
        groupName={groupName}
        isAutoFixable={isAutoFixable}
        vulnId={currentRow.id}
      />
    ),
    severity: handleSeverityInfo(severityUpdateRequest),
    trackingtreatments: <TreatmentTracking vuln={currentRow} />,
    treatments: (
      <TreatmentForm
        close={onClose}
        findingId={findingId}
        groupName={groupName}
        onActionCompleted={onActionCompleted}
        refetchData={refetchData}
        vulnerabilities={[currentRow]}
      />
    ),
  };
  const optionalTabs = _.compact([
    _.isNil(snippet) || _.isEmpty(snippet.content)
      ? undefined
      : {
          id: "code",
          isActive: currentTab === ("code" as TVulnsTabs),
          label: t("searchFindings.tabVuln.contentTab.code.title"),
          link: "/code",
          onClick: toTab,
          tooltip: t("searchFindings.tabVuln.contentTab.code.tooltip"),
        },
    currentRow.state === "VULNERABLE" &&
    isFindingReleased &&
    (canUpdateVulnsTreatment || canRequestZeroRiskVuln || canRemoveVulnsTags)
      ? {
          id: "treatments",
          isActive: currentTab === ("treatments" as TVulnsTabs),
          label: t("searchFindings.tabVuln.contentTab.treatments.title"),
          link: "/treatments",
          onClick: toTab,
          tooltip: t("searchFindings.tabVuln.contentTab.treatments.tooltip"),
        }
      : undefined,
    {
      id: "howtofix",
      isActive: currentTab === ("howtofix" as TVulnsTabs),
      label: t("searchFindings.tabVuln.contentTab.customFix.title"),
      link: "/howtofix",
      onClick: toTab,
      tooltip: t("searchFindings.tabVuln.contentTab.customFix.tooltip"),
    },
    isFindingReleased
      ? {
          id: "trackingtreatments",
          isActive: currentTab === ("trackingtreatments" as TVulnsTabs),
          label: t("searchFindings.tabVuln.contentTab.tracking.title"),
          link: "/trackingtreatments",
          onClick: toTab,
          tooltip: t("searchFindings.tabVuln.contentTab.tracking.tooltip"),
        }
      : undefined,
  ]);

  const fixOnClick = useCallback((event: React.MouseEvent): void => {
    event.preventDefault();
    setCurrentTab("howtofix" as TVulnsTabs);
  }, []);

  return (
    <Modal
      description={
        <VulnerabilityModalHeaderDescription
          currentRow={currentRow}
          cvssVersion={cvssVersion}
          labels={labels}
        />
      }
      modalRef={{ ...modalProps, close: closeModal, isOpen: isModalOpen }}
      otherActions={
        <Fragment>
          <IconButton
            icon={"wrench"}
            iconSize={"sm"}
            iconType={"fa-light"}
            id={"howtofix"}
            onClick={fixOnClick}
            tooltip={t("searchFindings.tabVuln.contentTab.customFix.tooltip")}
            variant={"ghost"}
          />
          <Authorize extraCondition={isAutoFixable}>
            <IconButton
              icon={"copy"}
              iconSize={"sm"}
              iconType={"fa-light"}
              onClick={onCopy}
              px={0.25}
              py={0.25}
              tooltip={t("searchFindings.copyUrl.tooltip")}
              variant={"ghost"}
            />
          </Authorize>
        </Fragment>
      }
      size={"lg"}
      tabItems={[
        {
          id: "details",
          isActive: currentTab === ("details" as TVulnsTabs),
          label: t("searchFindings.tabVuln.contentTab.details.title"),
          link: "/details",
          onClick: toTab,
          tooltip: t("searchFindings.tabVuln.contentTab.details.tooltip"),
        },
        {
          id: "severity",
          isActive: currentTab === ("severity" as TVulnsTabs),
          label: t("searchFindings.tabVuln.contentTab.severity.title"),
          link: "/severity",
          onClick: toTab,
          tooltip: t("searchFindings.tabVuln.contentTab.severity.tooltip"),
        },
      ].concat(optionalTabs)}
      title={
        <VulnerabilityModalHeaderTitle
          locationTitle={currentRow.where}
          origin={currentRow.origin}
        />
      }
    >
      <updateDescriptionContext.Provider value={value}>
        {_.isUndefined(currentRow) ? undefined : tabContent[currentTab]}
      </updateDescriptionContext.Provider>
    </Modal>
  );
};
export { ModalContent };
